import React, { useState } from "react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { CERTIFICATIONS } from "../data/content";
import { FadeUp, Stagger, StaggerItem } from "../components/Motion";
import { ShieldCheck, FileText, Briefcase, Rocket, X, Download } from "lucide-react";

const iconMap = { ShieldCheck, FileText, Briefcase, Rocket };

const Certifications = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeCert, setActiveCert] = useState(null);

  const openModal = (cert) => {
    setActiveCert(cert);
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => setActiveCert(null), 300);
    document.body.style.overflow = "unset";
  };

  const isoCert = CERTIFICATIONS.find(c => c.highlight);
  const otherCerts = CERTIFICATIONS.filter(c => !c.highlight);

  const IsoIcon = isoCert ? (iconMap[isoCert.icon] || ShieldCheck) : ShieldCheck;

  return (
    <>
      <SEO
        title="Certifications & Compliance — Rudrash Private Limited"
        description="Our verified standards, trusted registrations, and ISO 9001:2015 certification."
      />
      <div data-testid="page-certifications" className="bg-cream min-h-screen pb-24 md:pb-32">
        <PageHero
          eyebrow="Compliance"
          title="Verified standards & operations."
          subtitle="At Rudrash, quality and compliance are foundational. Explore our business registrations and operational certifications that ensure trust across our entire supply chain."
          image="https://images.pexels.com/photos/10269715/pexels-photo-10269715.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
        />

        <div className="container mx-auto px-6 md:px-10 xl:px-16 pt-16 md:pt-24 space-y-16 lg:space-y-24">
          
          {/* ISO Highlight */}
          {isoCert && (
            <FadeUp>
              <div className="border border-saffron/30 bg-cream-2 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="grid grid-cols-1 lg:grid-cols-12">
                  <div className="lg:col-span-5 bg-forest text-cream p-10 md:p-16 flex flex-col justify-center relative overflow-hidden">
                    <div className="grain" />
                    <IsoIcon className="relative z-10 w-16 h-16 md:w-20 md:h-20 text-saffron mb-8 opacity-90" strokeWidth={1.2} />
                    <h2 className="relative z-10 font-serif text-4xl md:text-5xl leading-tight">
                      ISO 9001:2015<br />Certified
                    </h2>
                    <div className="mt-8 relative z-10 flex">
                      <button 
                        onClick={() => openModal(isoCert)}
                        className="btn-accent"
                      >
                        Preview Certificate
                      </button>
                    </div>
                  </div>
                  <div className="lg:col-span-7 p-10 md:p-16 flex flex-col justify-center bg-cream">
                    <p className="eyebrow text-forest">{isoCert.shortDesc}</p>
                    <h3 className="mt-4 font-serif text-3xl md:text-4xl text-forest">{isoCert.title}</h3>
                    <p className="mt-6 text-muted-ink text-lg leading-relaxed max-w-2xl">
                      {isoCert.longDesc}
                    </p>
                  </div>
                </div>
              </div>
            </FadeUp>
          )}

          {/* Standard Grid */}
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherCerts.map((cert) => {
              const Icon = iconMap[cert.icon] || FileText;
              return (
                <StaggerItem key={cert.id}>
                  <div className="p-8 md:p-10 border border-border-soft bg-cream-2/40 h-full flex flex-col hover:bg-cream-2 transition-colors duration-300">
                    <div className="w-14 h-14 rounded-full bg-cream flex items-center justify-center shrink-0 text-forest border border-border-soft shadow-sm mb-8">
                      <Icon size={24} strokeWidth={1.5} />
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-serif text-2xl text-forest">{cert.title}</h3>
                      <p className="mt-4 text-muted-ink leading-relaxed">
                        {cert.longDesc}
                      </p>
                    </div>
                    <div className="mt-8 pt-8 border-t border-border-soft">
                      <button 
                        onClick={() => openModal(cert)}
                        className="text-saffron font-medium flex items-center gap-2 hover:text-forest transition-colors w-fit"
                      >
                        Preview Document
                      </button>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>

        {/* Modal Overlay */}
        <div 
          className={`fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10 transition-all duration-300 ${
            modalOpen ? "opacity-100 visible" : "opacity-0 invisible delay-200"
          }`}
        >
          {/* Backdrop */}
          <div 
            className={`absolute inset-0 bg-forest/80 backdrop-blur-sm transition-opacity duration-300 ${
              modalOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={closeModal}
          />
          
          {/* Modal Content */}
          <div 
            className={`relative w-full max-w-3xl bg-cream border border-border-soft shadow-2xl flex flex-col max-h-[90vh] transition-all duration-300 ${
              modalOpen ? "scale-100 translate-y-0" : "scale-95 translate-y-8"
            }`}
          >
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border-soft bg-cream-2">
              <div>
                <p className="text-xs font-semibold tracking-widest text-saffron uppercase">Document Preview</p>
                <h3 className="font-serif text-xl md:text-2xl text-forest mt-1">{activeCert?.title}</h3>
              </div>
              <button 
                onClick={closeModal}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-forest/5 text-forest transition-colors"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="overflow-y-auto p-6 flex-grow bg-cream flex flex-col items-center justify-center min-h-[40vh] md:min-h-[50vh]">
              {activeCert && (() => {
                if (activeCert.previewUrl) {
                  return (
                    <img 
                      src={activeCert.previewUrl} 
                      alt={activeCert.title} 
                      className="max-w-full h-auto max-h-[65vh] object-contain border border-border-soft shadow-sm bg-white" 
                      loading="lazy" 
                    />
                  );
                }

                const ActiveIcon = iconMap[activeCert.icon] || FileText;
                return (
                  <div className="text-center flex flex-col items-center">
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-cream-2 border border-border-soft flex items-center justify-center text-saffron/40 mb-8">
                      <ActiveIcon size={48} strokeWidth={1} className="md:w-16 md:h-16" />
                    </div>
                    <p className="text-muted-ink max-w-md">
                      Digital document preview is currently unavailable while physical copies are being formalized.
                    </p>
                    <p className="mt-4 font-serif text-forest text-lg">
                      {activeCert.shortDesc}
                    </p>
                  </div>
                );
              })()}
            </div>
            
            <div className="p-4 md:p-6 border-t border-border-soft bg-cream-2 flex justify-end">
              {activeCert?.pdfUrl ? (
                <a 
                  href={activeCert.pdfUrl} 
                  target="_blank" 
                  rel="noreferrer"
                  className="btn-primary flex items-center gap-2"
                >
                  <Download size={16} /> Download PDF
                </a>
              ) : (
                <button className="btn-primary opacity-50 cursor-not-allowed flex items-center gap-2">
                  <Download size={16} /> Download PDF
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Certifications;
